/**
 * New asset (`/assets/new`, `asset.manage`), started one of two ways:
 *
 * - **By hand**: the asset's fields in one form. **Create draft** makes the draft with its tags and experts and opens
 *   the editor, where the kind's suggested sections are written one by one.
 * - **From files** (`?from=files`, also `asset.view.internal`: the AI and the reviewer read the files whole): see
 *   `from-files/FromFilesFlow.tsx`.
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileUp, Lock, PenLine } from 'lucide-react';

import { useCreateAsset } from '@/api/queries';
import { bannerError, serverFieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { PageHead } from '@/ui/PageHead';
import { FormError, StateCard } from '@/ui/StateCard';

import { AssetFieldsForm, FIELD_PATHS } from './fields/AssetFieldsForm';
import { type AssetFieldsDraft, createBody, EMPTY_FIELDS, fieldsValid } from './fields/assetFields';
import { FromFilesFlow } from './from-files/FromFilesFlow';

function ByHand() {
  const navigate = useNavigate();
  const create = useCreateAsset();
  const [draft, setDraft] = useState<AssetFieldsDraft>(EMPTY_FIELDS);
  return (
    <form
      className="card asset-form"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(createBody(draft), { onSuccess: (asset) => navigate(`/assets/${asset.id}/edit`) });
      }}
    >
      <div className="card-body stack-md">
        <FormError error={bannerError(create.error, FIELD_PATHS)} />
        <AssetFieldsForm draft={draft} set={(patch) => setDraft((d) => ({ ...d, ...patch }))} errors={serverFieldErrors(create.error)} />
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/assets')}>Cancel</button>
          <ActionButton state={buttonState(create)} label="Create draft" pendingLabel="Creating…" doneLabel="Created" type="submit" disabled={!fieldsValid(draft)} />
        </div>
      </div>
    </form>
  );
}

function ModeSwitch({ fromFiles, onChange }: { fromFiles: boolean; onChange: (fromFiles: boolean) => void }) {
  return (
    <div className="segmented from-files-mode" role="radiogroup" aria-label="How to start the asset">
      <button type="button" role="radio" aria-checked={!fromFiles} onClick={() => onChange(false)}>
        <PenLine aria-hidden="true" /> By hand
      </button>
      <button type="button" role="radio" aria-checked={fromFiles} onClick={() => onChange(true)}>
        <FileUp aria-hidden="true" /> From files
      </button>
    </div>
  );
}

export function NewAssetPage() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const filesAllowed = can('asset.view.internal');
  const fromFiles = filesAllowed && params.get('from') === 'files';
  const back = { to: '/assets', name: 'Assets' };

  if (!can('asset.manage')) {
    return (
      <>
        <PageHead title="New asset" back={back} />
        <StateCard icon={<Lock aria-hidden="true" />} title="You cannot add assets">
          <p>Asset managers add and edit the catalog.</p>
        </StateCard>
      </>
    );
  }

  const subtitle = fromFiles
    ? 'Upload files; the AI drafts the asset and you review it before anything is saved.'
    : 'It starts as a draft; you write its sections next.';
  return (
    <>
      <PageHead title="New asset" subtitle={subtitle} back={back} />
      {filesAllowed && <ModeSwitch fromFiles={fromFiles} onChange={(files) => setParams(files ? { from: 'files' } : {}, { replace: true })} />}
      {fromFiles ? <FromFilesFlow /> : <ByHand />}
    </>
  );
}
